// Flat row filter for the issue register's FilterChipBar (Status / Severity /
// Owner / Age) plus the search box. Issues have no parent/child grouping, so
// this is a straight predicate over a flat Issue[] — far simpler than the
// deliverable grouped filter.
//
// Search reuses the deliverable searchQuery parser (AND of bare words,
// "quoted phrases", -exclusions) but matches against issueRef AND title so a
// contract manager can find ISS-014 by its ref or by words in its title. Age
// buckets derive from the issue's age in days against a pinned `today`.
// Owner supports an `__unassigned__` token so issues with no owner are
// filterable as a group.

import {
  parseSearchQuery,
  type ParsedSearchQuery,
} from "../deliverable/searchQuery";
import type {
  Issue,
  IssueLinkTargetType,
  IssueSeverity,
  IssueStatus,
} from "../../../../types";

/** Sentinel owner value matching issues with no ownerId. */
export const UNASSIGNED_OWNER_TOKEN = "__unassigned__";

export type IssueAgeBucket = "recent" | "1-4w" | "over-30d";

/** Deep-link "Linked to" filter — set when the user lands via
 *  `?tab=issue&linkedType=X&linkedId=Y` from a back-link badge. Restricts
 *  the register to issues whose `links` include the (targetType, targetId)
 *  pair. Distinct from the `?item=` deep-link, which opens a specific issue
 *  in the drawer; the two can coexist. */
export interface IssueLinkedFilter {
  targetType: IssueLinkTargetType;
  targetId: string;
}

export interface IssueChipFilters {
  status: IssueStatus[];
  severity: IssueSeverity[];
  /** Owner user ids, plus the UNASSIGNED_OWNER_TOKEN sentinel. */
  owner: string[];
  age: IssueAgeBucket[];
  linked: IssueLinkedFilter | null;
}

export const EMPTY_ISSUE_FILTERS: IssueChipFilters = {
  status: [],
  severity: [],
  owner: [],
  age: [],
  linked: null,
};

const DAY_MS = 86_400_000;

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function ageBucketOf(issue: Issue, today: Date): IssueAgeBucket | null {
  const raised = isoToUTCDay(issue.raisedAt);
  if (raised === null) return null;
  const ageDays = Math.round((startOfUTCDay(today) - raised) / DAY_MS);
  if (ageDays <= 7) return "recent";
  if (ageDays <= 30) return "1-4w";
  return "over-30d";
}

function matchesStatus(issue: Issue, selected: IssueStatus[]): boolean {
  return selected.length === 0 || selected.includes(issue.status);
}

function matchesSeverity(issue: Issue, selected: IssueSeverity[]): boolean {
  return selected.length === 0 || selected.includes(issue.severity);
}

function matchesOwner(issue: Issue, selected: string[]): boolean {
  if (selected.length === 0) return true;
  if (issue.ownerId == null) return selected.includes(UNASSIGNED_OWNER_TOKEN);
  return selected.includes(issue.ownerId);
}

function matchesAge(
  issue: Issue,
  selected: IssueAgeBucket[],
  today: Date,
): boolean {
  if (selected.length === 0) return true;
  const bucket = ageBucketOf(issue, today);
  return bucket !== null && selected.includes(bucket);
}

function matchesLinked(
  issue: Issue,
  linked: IssueLinkedFilter | null,
): boolean {
  if (!linked) return true;
  return (issue.links ?? []).some(
    (l) => l.targetType === linked.targetType && l.targetId === linked.targetId,
  );
}

function matchesSearch(issue: Issue, query: ParsedSearchQuery): boolean {
  const haystack = `${issue.issueRef}\n${issue.title}`.toLowerCase();
  for (const term of query.include) {
    if (!haystack.includes(term)) return false;
  }
  for (const term of query.exclude) {
    if (haystack.includes(term)) return false;
  }
  return true;
}

export function applyIssueChipFilters(
  issues: Issue[],
  filters: IssueChipFilters,
  search: string,
  today: Date = new Date(),
): Issue[] {
  const query = parseSearchQuery(search);
  const noSearch = query.include.length === 0 && query.exclude.length === 0;
  const noChips =
    filters.status.length === 0 &&
    filters.severity.length === 0 &&
    filters.owner.length === 0 &&
    filters.age.length === 0 &&
    filters.linked === null;
  // Fast path: nothing selected returns the same reference so callers can
  // skip a re-render via identity comparison.
  if (noSearch && noChips) return issues;

  return issues.filter(
    (issue) =>
      matchesStatus(issue, filters.status) &&
      matchesSeverity(issue, filters.severity) &&
      matchesOwner(issue, filters.owner) &&
      matchesAge(issue, filters.age, today) &&
      matchesLinked(issue, filters.linked) &&
      matchesSearch(issue, query),
  );
}
